import { useState, useEffect, useCallback, useRef } from 'react'

const SESSION_DURATION = 30 * 60 * 1000 // 30 minutos
const WARNING_DURATION = 5 * 60 * 1000   // 5 minutos de aviso

interface UseSessionTimeoutReturn {
  showWarning: boolean
  timeLeft: number // segundos
  timeLeftFormatted: string
  warningProgress: number
  extendSession: () => void
  forceLogout: () => void
}

export function useSessionTimeout(
  onLogout: () => void,
  enabled = true
): UseSessionTimeoutReturn {
  const [showWarning, setShowWarning] = useState(false)
  const [timeLeft, setTimeLeft] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastActivityRef = useRef(Date.now())

  const clearTimers = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
  }, [])

  const resetTimers = useCallback(() => {
    clearTimers()
    lastActivityRef.current = Date.now()
    setShowWarning(false)

    // Timer para mostrar warning
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true)
      setTimeLeft(Math.floor(WARNING_DURATION / 1000))
    }, SESSION_DURATION - WARNING_DURATION)

    // Timer para logout
    timerRef.current = setTimeout(() => {
      onLogout()
    }, SESSION_DURATION)
  }, [clearTimers, onLogout])

  // Monitorar atividade do usuário
  useEffect(() => {
    if (!enabled) return

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']

    const handleActivity = () => {
      const now = Date.now()
      // Debounce: só reseta se passou pelo menos 1 segundo desde a última atividade
      if (now - lastActivityRef.current > 1000) {
        resetTimers()
      }
    }

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    // Iniciar timers
    resetTimers()

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
      clearTimers()
    }
  }, [enabled, resetTimers, clearTimers])

  // Contagem regressiva do warning
  useEffect(() => {
    if (!showWarning) return

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          onLogout()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [showWarning, onLogout])

  const extendSession = useCallback(() => {
    resetTimers()
  }, [resetTimers])

  const forceLogout = useCallback(() => {
    clearTimers()
    onLogout()
  }, [clearTimers, onLogout])

  const minutes = Math.floor(timeLeft / 60)
  const seconds = timeLeft % 60
  const timeLeftFormatted = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  const warningProgress = showWarning ? (timeLeft / (WARNING_DURATION / 1000)) * 100 : 100

  return {
    showWarning,
    timeLeft,
    timeLeftFormatted,
    warningProgress,
    extendSession,
    forceLogout,
  }
}
